import React, { memo, useEffect, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import {
  useIsSmartSuggestionSettingEnabled,
  useLabelSuggestion
} from 'modules/timesheet-labels/hooks';
import { LabelType, LabelValueSource } from 'graphql/types';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { getCustomLabel } from 'modules/notes/common/utils';
import { useFeatureFlag } from 'modules/common/hooks';
import { CustomLabelsField, useCustomLabelArray } from '../hooks';
import { UpdateCustomLabel } from './UpdateCustomLabel';
import { AddCustomLabel } from './AddCustomLabel';
import { CustomLabelMoreAction } from './CustomLabelMoreAction';

type classesProps = {
  classes?: ClassNameMap<'text' | 'root'>;
  isSmartSuggestionEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    margin: theme.spacing(2, 2.5, 1, 2.5),
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.spacing(0.75),
    alignItems: 'flex-start',
    padding: theme.spacing(0.75, 0),
    flexDirection: 'column',
    width: '100vw'
  },
  text: {
    color: theme.palette.grey[700],
    width: 'fit-content',
    backgroundColor: theme.palette.background.default,
    marginTop: theme.spacing(-2),
    marginLeft: theme.spacing(1.25),
    padding: theme.spacing(0, 0.5)
  },
  labelsContainer: {
    display: 'flex',
    flexFlow: 'wrap'
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ({ isSmartSuggestionEnabled }: classesProps) =>
      isSmartSuggestionEnabled ? theme.spacing(0, 1.5, 0, 0) : undefined
  }
}));

type CustomLabelsProps = {
  fieldName: string;
  overrideClasses?: ClassNameMap<'text' | 'root'>;
};

const CustomLabelsFC: React.FC<CustomLabelsProps> = ({
  fieldName,
  overrideClasses
}: CustomLabelsProps) => {
  const { getSuggestions } = useLabelSuggestion();
  const { getValues } = useFormContext();
  const {
    customLabels: CustomLabelValues,
    addCustomLabel,
    removeCustomLabel,
    updateCustomLabel,
    replace
  } = useCustomLabelArray({ fieldName });

  const {
    labelSuggestions,
    disableSuggestions
  } = useTimesheetLabelSuggestionContext();
  const customLabels = useWatch({
    name: `${fieldName}`,
    defaultValue: CustomLabelValues
  });

  const isSmartSuggestionEnabled =
    useIsSmartSuggestionSettingEnabled() && !disableSuggestions;
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );

  const classes = useStyles({
    classes: overrideClasses,
    isSmartSuggestionEnabled
  });
  const suggestedCustomLables = useMemo(() => {
    return labelSuggestions?.labelSuggestions?.suggestions.filter(
      (label) =>
        label.label.type === LabelType.Textselect && label.suggestedValues[0]
    );
  }, [labelSuggestions?.labelSuggestions.suggestions]);

  useEffect(() => {
    if (suggestedCustomLables && isSmartSuggestionEnabled) {
      const labels = getValues(fieldName);
      const selectedCustomLabels = labels.filter(
        (item: CustomLabel) =>
          item.valueSource === LabelValueSource.User ||
          (isLabelLevelAcceptedByEnabled && item.acceptedBy)
      );
      const customLabels = getCustomLabel(
        suggestedCustomLables,
        selectedCustomLabels,
        getSuggestions,
        labelSuggestions
      );
      replace(customLabels);
    }
  }, [
    fieldName,
    getSuggestions,
    getValues,
    isSmartSuggestionEnabled,
    isLabelLevelAcceptedByEnabled,
    labelSuggestions,
    replace,
    suggestedCustomLables
  ]);
  return (
    <div className={classes.container}>
      <div data-testid="custom-labels-list" className={classes.root}>
        <Typography variant="caption" className={classes.text}>
          <FormattedMessage id="customLabels.customLabel" />
        </Typography>
        <div className={classes.labelsContainer}>
          {customLabels?.map((field: CustomLabelsField, position: number) => (
            <UpdateCustomLabel
              key={field.key}
              label={field}
              position={position}
              updateCustomLabel={updateCustomLabel}
              removeCustomLabel={removeCustomLabel}
              fieldName={fieldName}
            />
          ))}
          <AddCustomLabel
            addCustomLabel={addCustomLabel}
            fieldName={fieldName}
          />
        </div>
      </div>
      {isSmartSuggestionEnabled && (
        <CustomLabelMoreAction fieldName={fieldName} />
      )}
    </div>
  );
};

export const CustomLabels = memo(CustomLabelsFC);
